import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OFFICIAL_POOL_LIMITS, TARGET_LEAGUES, type CalculatorMarket, type CalculatorMatchSelection } from '@nowscore/core';
import {
  accessLogout,
  AUTH_INVALID_EVENT,
  getCurrentLotteryDay,
  getDaySummary,
  getMatchDetail,
  getNextMatchDay,
  getRole,
  getSyncStatus,
  getToken,
  recommendM1Parlay,
  triggerCheck,
  type MatchDetailDto,
  type MatchDto,
  type ParlayRecommendDto,
} from './api';
import { LoginScreen } from './components/Login';
import { DateBar, LeagueBar, TopBar } from './components/Chrome';
import { DetailSheet } from './components/DetailSheet';
import { EmptyState, ErrorState, PullIndicator, SkeletonList, ToastHost } from './components/Feedback';
import { FilterSheet } from './components/FilterSheet';
import { MatchCard } from './components/MatchCard';
import { LotteryCalculatorBar } from './components/LotteryCalculatorBar';
import { LotteryMarketTabs } from './components/LotteryMarketGrid';
import { AnalysisDrawer, type AnalysisTarget } from './components/AnalysisDrawer';
import { DailyOverview } from './components/DailyOverview';
import { FavoritesPanel } from './components/FavoritesPanel';
import { AnalysisHistoryPanel } from './components/AnalysisHistoryPanel';
const M1ParlayResult = lazy(() => import('./components/M1').then((mod) => ({ default: mod.M1ParlayResult })));
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { useToast } from './hooks/useToast';
import { batchLabel, businessDateToday, shiftDate } from './lib/format';
import { type OddsView } from './lib/odds';
import { DEFAULT_PREFS, readDayCache, readPrefs, writeDayCache, writePrefs, type DayData, type UiPrefs } from './lib/storage';

// 管理面板只对管理员可见：懒加载，避免拖慢首屏（首屏 JS 预算 ≤100KB gzip）。
const AdminDashboard = lazy(() => import('./components/Admin').then((mod) => ({ default: mod.AdminDashboard })));
const AdminPanel = lazy(() => import('./components/Admin').then((mod) => ({ default: mod.AdminPanel })));

/** 空数据占位（详情接口失败时仍可展示卡片骨架信息）。 */
function emptyDetail(match: MatchDto): MatchDetailDto {
  const team = (teamId: string) => ({ teamId, played: 0, wins: 0, draws: 0, losses: 0, goalsFor: 0, goalsAgainst: 0, goalDifference: 0, goalsForAvg: 0, goalsAgainstAvg: 0, over25Rate: 0, bttsRate: 0, cleanSheetRate: 0, failedToScoreRate: 0, pointsPerGame: 0, form: [] });
  return {
    ...match,
    odds: [],
    movement: [],
    stats: [],
    liveSnapshots: [],
    recent: { home: [], away: [] },
    sourceAnalysis: { scoreAll: [], scoreHome: [], scoreGuest: [], halfAll: [], halfHome: [], halfGuest: [] },
    analysis: { home: team(match.homeTeamId), away: team(match.awayTeamId), headToHead: { played: 0, matches: [] } },
  };
}

function calculatorMaxPass(selections: Record<string, CalculatorMatchSelection>): number {
  const active = Object.values(selections);
  if (active.length === 0 || active.some((item) => item.picks.length > 1)) return 1;
  const markets = active.map((item) => item.picks[0]?.market).filter(Boolean) as CalculatorMarket[];
  const sameMarket = markets.length > 0 && markets.every((market) => market === markets[0]);
  return Math.min(8, Math.max(1, active.length), sameMarket ? OFFICIAL_POOL_LIMITS[markets[0]!] : 8);
}
export function App() {
  const autoAdvancedRef = useRef(false);
  const syncRetryRef = useRef<number | null>(null);
  const [role, setRole] = useState<'admin' | 'user' | ''>(getRole());
  const [accessToken, setAccessToken] = useState(getToken());
  const [authNotice, setAuthNotice] = useState('');
  const [date, setDate] = useState(businessDateToday());
  const [businessDay, setBusinessDay] = useState(businessDateToday());
  const [prefs, setPrefs] = useState<UiPrefs>(() => readPrefs());
  const [dayData, setDayData] = useState<DayData>(() => readDayCache(businessDateToday()) ?? { matches: [], statusCounts: {} });
  const dayDataRef = useRef(dayData);
  const [selectedMatch, setSelectedMatch] = useState<MatchDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [calculatorSelections, setCalculatorSelections] = useState<Record<string, CalculatorMatchSelection>>({});
  const [activeLotteryMarket, setActiveLotteryMarket] = useState<CalculatorMarket>('had');
  const [calculatorPassType, setCalculatorPassType] = useState(1);
  const [calculatorMultiplier, setCalculatorMultiplier] = useState(1);
  const [m1Parlay, setM1Parlay] = useState<ParlayRecommendDto | null>(null);
  const [m1Busy, setM1Busy] = useState(false);
  const [loading, setLoading] = useState(() => readDayCache(businessDateToday()) === null);
  const [error, setError] = useState('');
  const [syncText, setSyncText] = useState('准备检查增量数据…');
  const [showAdmin, setShowAdmin] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [analysisTarget, setAnalysisTarget] = useState<AnalysisTarget | null>(null);
  const [analysisHistoryId, setAnalysisHistoryId] = useState<string | null>(null);
  const [showDaily, setShowDaily] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const toast = useToast();
  useEffect(() => {
    const expire = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      setRole('');
      setAccessToken('');
      setAuthNotice(detail?.message ? '登录已失效：' + detail.message : '登录已失效，请重新输入六位口令');
    };
    window.addEventListener(AUTH_INVALID_EVENT, expire);
    return () => window.removeEventListener(AUTH_INVALID_EVENT, expire);
  }, []);

  const updatePrefs = useCallback((patch: Partial<UiPrefs>) => {
    setPrefs((current) => ({ ...current, ...patch }));
    writePrefs(patch);
  }, []);

  const load = useCallback(async () => {
    if (!accessToken || !role) return;
    if (syncRetryRef.current !== null) { window.clearTimeout(syncRetryRef.current); syncRetryRef.current = null; }
    const cached = readDayCache(date);
    if (cached) { setDayData(cached); setLoading(false); } else { setLoading(true); }
    setError('');
    try {
      const data = await getDaySummary(date);
      if (!autoAdvancedRef.current && date === businessDateToday() && !data.matches.some((match) => match.status === 'scheduled' || match.status === 'live')) {
        const next = await getNextMatchDay(date, 7);
        if (next?.date && next.date !== date) {
          autoAdvancedRef.current = true;
          setDate(next.date);
          return;
        }
        autoAdvancedRef.current = true;
      }
      setDayData({ matches: data.matches, statusCounts: data.statusCounts });
      writeDayCache(date, { matches: data.matches, statusCounts: data.statusCounts });
      setLoading(false);
      if (data.matches.length > 0) {
        window.setTimeout(() => {
          void (async () => {
            for (const target of [shiftDate(date, -1), shiftDate(date, 1)]) {
              if (readDayCache(target)) continue;
              try {
                const prefetched = await getDaySummary(target);
                if (prefetched.matches.length > 0) writeDayCache(target, { matches: prefetched.matches, statusCounts: prefetched.statusCounts });
              } catch { /* 预取失败不影响主流程 */ }
            }
          })();
        }, 500);
      }
      if (data.syncing) {
        setSyncText('竞彩数据补采中…');
        syncRetryRef.current = window.setTimeout(() => { syncRetryRef.current = null; void load(); }, 5000);
      }
      try {
        const status = await getSyncStatus();
        const active = status.jobs.filter((item) => item.status === 'queued' || item.status === 'leased').reduce((sum, item) => sum + Number(item.count ?? 0), 0);
        setSyncText(data.syncing ? '竞彩数据补采中…' : active > 0 ? '后台同步中 · 数据可正常查看' : '数据已就绪');
      } catch {
        setSyncText('数据已加载 · 同步状态暂不可用');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '无法连接数据服务');
      setLoading(false);
    }
  }, [accessToken, date, role]);

  const refreshLive = useCallback(async () => {
    await load();
    void triggerCheck().catch(() => undefined);
  }, [load]);

  const manualRefresh = useCallback(async () => {
    try {
      await refreshLive();
      toast.push('已刷新最新数据', 'success');
    } catch (caught) {
      toast.push(caught instanceof Error ? caught.message : '刷新失败', 'error', { label: '重试', run: () => { void manualRefresh(); } });
    }
  }, [refreshLive, toast]);

  const pull = usePullToRefresh(async () => {
    await refreshLive();
    toast.push('已刷新最新数据', 'success');
  }, { enabled: Boolean(accessToken && role) && !selectedMatch && !filterOpen && !analysisTarget && !showDaily && !showFavorites && !showHistory && !showAdmin && !showDashboard });

  useEffect(() => {
    if (!accessToken || !role) return;
    void (async () => {
      try {
        const current = await getCurrentLotteryDay();
        if (!current?.businessDate) return;
        setBusinessDay(current.businessDate);
        if (!autoAdvancedRef.current) { autoAdvancedRef.current = true; setDate(current.businessDate); }
      } catch { /* 保留本地营业日兜底 */ }
    })();
  }, [accessToken, role]);

  useEffect(() => { dayDataRef.current = dayData; }, [dayData]);

  useEffect(() => {
    if (!accessToken || !role) return;
    const cached = readDayCache(date);
    if (cached) { setDayData(cached); setLoading(false); } else { setLoading(true); }
    void refreshLive();
    const timer = window.setInterval(() => {
      if (dayDataRef.current.matches.some((match) => match.status === 'live') || date === businessDateToday()) void refreshLive();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [date, accessToken, role, refreshLive]);

  useEffect(() => () => { if (syncRetryRef.current !== null) window.clearTimeout(syncRetryRef.current); }, []);

  const openMatch = useCallback(async (match: MatchDto) => {
    setSelectedMatch(emptyDetail(match));
    setDetailLoading(true);
    setDetailError('');
    try {
      const detail = await getMatchDetail(match.scheduleId);
      setSelectedMatch(detail);
    } catch (caught) {
      setDetailError(caught instanceof Error ? caught.message : '详情加载失败');
    } finally {
      setDetailLoading(false);
    }
  }, []);
  const updateCalculatorSelection = useCallback((selection: CalculatorMatchSelection) => {
    setCalculatorSelections((current) => {
      const next = { ...current };
      if (selection.picks.length === 0) delete next[selection.scheduleId];
      else next[selection.scheduleId] = selection;
      const maxPass = calculatorMaxPass(next);
      setCalculatorPassType((pass) => pass > maxPass ? 1 : pass);
      return next;
    });
  }, []);
  const removeCalculatorSelection = useCallback((scheduleId: string) => {
    setCalculatorSelections((current) => {
      const next = { ...current };
      delete next[scheduleId];
      const maxPass = calculatorMaxPass(next);
      setCalculatorPassType((pass) => pass > maxPass ? 1 : pass);
      return next;
    });
  }, []);  const runM1Parlay = useCallback(async () => {
    setM1Busy(true);
    try {
      setM1Parlay(await recommendM1Parlay(businessDay));
    } catch (caught) {
      toast.push(caught instanceof Error ? caught.message : 'M1 串关推荐失败', 'error');
    } finally {
      setM1Busy(false);
    }
  }, [businessDay, toast]);

  const logout = async () => { await accessLogout(); setRole(''); setAccessToken(''); window.location.reload(); };

  const visible = useMemo(() => {
    const list = dayData.matches
      .filter((match) => match && match.live && match.score && match.lottery !== undefined)
      .filter((match) => (prefs.statusFilter === 'all' || match.status === prefs.statusFilter) && (prefs.leagueFilter === 'all' || match.leagueCode === prefs.leagueFilter));
    return prefs.sortMode === 'league'
      ? [...list].sort((left, right) => (left.lottery?.number ?? '').localeCompare(right.lottery?.number ?? ''))
      : list;
  }, [dayData.matches, prefs.statusFilter, prefs.leagueFilter, prefs.sortMode]);

  const selectedCalculatorMatches = useMemo(() => dayData.matches.filter((match) => Boolean(calculatorSelections[match.scheduleId])), [dayData.matches, calculatorSelections]);
  const activeFilterCount = (prefs.statusFilter !== 'all' ? 1 : 0) + (prefs.oddsView !== 'compare' ? 1 : 0) + (prefs.sortMode !== 'time' ? 1 : 0);

  if (!accessToken || !role) {
    return <LoginScreen notice={authNotice} onLogin={(result) => { setAuthNotice(''); setRole(result.role); setAccessToken(getToken()); }} />;
  }

  const analyzeMatch = (match: MatchDto) => { setAnalysisHistoryId(null); setAnalysisTarget({ scheduleId: match.scheduleId, homeTeamName: match.homeTeamName, awayTeamName: match.awayTeamName, kickoffTime: match.kickoffTime, lotteryNumber: match.lottery?.number ?? null }); };

  return <main className="app-shell">
    <div className="app-sticky">
      <TopBar
        syncText={syncText}
        syncing={syncText.includes('补采') || syncText.includes('同步中')}
        role={role}
        refreshing={loading || pull.refreshing}
        onRefresh={() => void manualRefresh()}
        onOpenDashboard={() => setShowDashboard((value) => !value)}
        onOpenAdmin={() => setShowAdmin((value) => !value)}
        onLogout={() => void logout()}
      />
      <DateBar
        date={date}
        businessDay={businessDay}
        count={dayData.matches.length}
        onShift={(offset) => setDate(shiftDate(date, offset))}
        onPick={(value) => value && setDate(value)}
        onToday={() => setDate(businessDay)}
      />
      <div className='quick-analysis-bar'><button type='button' onClick={() => setShowDaily(true)}>今日官方竞彩</button><button type='button' onClick={() => setShowFavorites(true)}>我的收藏</button><button type='button' onClick={() => setShowHistory(true)}>分析历史</button></div>
      <LotteryMarketTabs value={activeLotteryMarket} onChange={setActiveLotteryMarket} label='主界面官方竞彩玩法' />
      <LeagueBar
        value={prefs.leagueFilter}
        leagues={TARGET_LEAGUES}
        filterCount={activeFilterCount}
        onChange={(code) => updatePrefs({ leagueFilter: code })}
        onOpenFilters={() => setFilterOpen(true)}
        onRunM1={() => void runM1Parlay()}
        m1Busy={m1Busy}
      />
    </div>

    <PullIndicator distance={pull.distance} refreshing={pull.refreshing} />

    <Suspense fallback={null}>
      {role === 'admin' && showDashboard && <AdminDashboard onClose={() => setShowDashboard(false)} />}
      {role === 'admin' && showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
    </Suspense>

    {loading && <SkeletonList />}
    {!loading && error && <ErrorState message={error} onRetry={() => void manualRefresh()} />}
    {!loading && !error && visible.length === 0 && (
      dayData.matches.length === 0
        ? <EmptyState title="该营业日暂无竞彩比赛" hint="切换到有竞彩场次的日期会自动按需抓取赔率。" actionLabel="回到今天" onAction={() => setDate(businessDay)} />
        : <EmptyState title="当前筛选没有比赛" hint={'当日共 ' + dayData.matches.length + ' 场，筛选后为 0 场。试试切换联赛或状态。'} actionLabel="清除筛选" onAction={() => updatePrefs({ ...DEFAULT_PREFS })} />
    )}
    {!loading && !error && visible.length > 0 && <div className="match-list">
      {visible.map((match) => <MatchCard
        key={match.scheduleId}
        match={match}
        activeMarket={activeLotteryMarket}
        oddsView={prefs.oddsView as OddsView}
        calculatorSelection={calculatorSelections[match.scheduleId] ?? null}
        onCalculatorSelectionChange={updateCalculatorSelection}
        onOpen={openMatch}
        onAnalyze={analyzeMatch}
      />)}
    </div>}

    {selectedCalculatorMatches.length > 0 && <LotteryCalculatorBar matches={selectedCalculatorMatches} selections={calculatorSelections} passType={calculatorPassType} multiplier={calculatorMultiplier} onPassTypeChange={setCalculatorPassType} onMultiplierChange={setCalculatorMultiplier} onRemove={removeCalculatorSelection} onClear={() => { setCalculatorSelections({}); setCalculatorPassType(1); setCalculatorMultiplier(1); }} />}

    <Suspense fallback={null}>{m1Parlay && <M1ParlayResult result={m1Parlay} />}</Suspense>

    {selectedMatch && <DetailSheet
      match={selectedMatch}
      oddsView={prefs.oddsView as OddsView}
      onOddsViewChange={(view) => updatePrefs({ oddsView: view })}
      onAnalyze={() => analyzeMatch(selectedMatch)}
      calculatorSelection={calculatorSelections[selectedMatch.scheduleId] ?? null}
      onCalculatorSelectionChange={updateCalculatorSelection}
      loading={detailLoading}
      error={detailError}
      onClose={() => { setSelectedMatch(null); setDetailLoading(false); setDetailError(""); }}
    />}

    <FilterSheet
      open={filterOpen}
      prefs={prefs}
      onChange={updatePrefs}
      onReset={() => updatePrefs({ ...DEFAULT_PREFS, leagueFilter: prefs.leagueFilter })}
      onClose={() => setFilterOpen(false)}
    />

    {analysisTarget && <AnalysisDrawer target={analysisTarget} historyId={analysisHistoryId} onClose={() => { setAnalysisTarget(null); setAnalysisHistoryId(null); }} />}
    <DailyOverview open={showDaily} date={date} league={prefs.leagueFilter} matches={dayData.matches} activeMarket={activeLotteryMarket} selections={calculatorSelections} onMarketChange={setActiveLotteryMarket} onSelectionChange={updateCalculatorSelection} onClose={() => setShowDaily(false)} onAnalyze={(item) => { setShowDaily(false); setAnalysisHistoryId(null); setAnalysisTarget({ scheduleId: item.scheduleId, homeTeamName: item.homeTeamName, awayTeamName: item.awayTeamName, kickoffTime: item.kickoffTime, lotteryNumber: item.lottery?.number ?? null }); }} />
    <FavoritesPanel open={showFavorites} onClose={() => setShowFavorites(false)} onAnalyze={(row) => { setShowFavorites(false); setAnalysisHistoryId(null); setAnalysisTarget({ scheduleId: row.snapshot.match.scheduleId, homeTeamName: row.snapshot.match.homeTeamName, awayTeamName: row.snapshot.match.awayTeamName, kickoffTime: row.snapshot.match.kickoffTime, lotteryNumber: row.snapshot.match.lotteryNumber }); }} />
    <AnalysisHistoryPanel open={showHistory} onClose={() => setShowHistory(false)} onView={(row, historyId) => { setShowHistory(false); setAnalysisHistoryId(historyId); setAnalysisTarget({ scheduleId: row.scheduleId, homeTeamName: row.match?.homeTeamName ?? row.scheduleId, awayTeamName: row.match?.awayTeamName ?? '', kickoffTime: row.match?.kickoffTime ?? row.createdAt, lotteryNumber: row.match?.lotteryNumber ?? null }); }} />
    <ToastHost toasts={toast.toasts} onDismiss={toast.dismiss} />

    <footer className="app-footer">竞彩赔率 · 威廉/立博对照 · 数据不足时显示明确原因</footer>
  </main>;
}
